// Ticket detail — subject/body, comment thread, and inline controls to change
// status / priority / category / assignee (PATCH /tickets/:id). Plus three rich
// panels fed by the agency GET /tickets/:id (TicketDetailFull): submit-time
// Context, Attachments (screenshots + files), and What they've tried (viewing
// history) so the agent sees exactly what the requester saw before asking.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  AgencyUser,
  TicketDetailFull,
  TicketContext,
  TicketAttachment,
  ViewedContent,
  RequesterContext,
  TimeEntry,
  TimeIntegrity,
  RunningTimer,
  WorkflowRevision,
} from '../../lib/suite-types';
import { agencyApi, actingAgentId, type TicketsMetaResponse } from '../../lib/agencyApi';
import { authHeaders } from '../../lib/session';
import { Button, Card, Chip, ErrorNote, Select, Spinner, StatTile, Toggle } from './ui';
import { formatApiTime, timeAgo } from '../../lib/time';

const UNASSIGNED = '__pot__';

// ── formatters ─────────────────────────────────────────────────────
const DASH = '—';

// Bytes → human size for file attachments.
function fmtSize(bytes: number | null): string {
  if (bytes == null) return DASH;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

// Seconds → HH:MM:SS (live timer + logged durations).
function fmtHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return [h, m, s % 60].map((n) => String(n).padStart(2, '0')).join(':');
}

// Seconds → m:ss (watch time for videos).
function fmtWatch(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

// Seconds → coarse duration for integrity chips: "31m" under an hour, "1:05" (H:MM) above.
function fmtDur(totalSeconds: number): string {
  const mins = Math.max(0, Math.round(totalSeconds / 60));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}:${String(mins % 60).padStart(2, '0')}`;
}

// See lib/time.ts — this was a private copy that parsed zone-less D1 timestamps as local time.
const fmtRelative = (iso: string) => timeAgo(iso, { never: DASH });

const CONTENT_ACCENT: Record<ViewedContent['type'], string> = {
  doc: '#2563eb',
  video: '#9333ea',
  tour: '#0d9488',
};

const AMBER = '#d97706';

// ── Integrity chips ────────────────────────────────────────────────
// Calm, compact time-integrity signals for a single logged entry. Renders
// nothing when integrity is absent or clean (no flags) — we only surface a
// warning when the backend actually flagged the entry. Amber = attention,
// neutral = context (the interacted share of the tracked time).
function IntegrityChips({ integrity }: { integrity?: TimeIntegrity | null }) {
  if (!integrity || !integrity.flags?.length) return null;
  const { flags, idleSeconds, offAccountSeconds, activePct } = integrity;
  const has = (f: TimeIntegrity['flags'][number]) => flags.includes(f);
  return (
    <>
      {(has('idle') || has('high_idle')) && idleSeconds > 0 && (
        <Chip label={`⚠ ${fmtDur(idleSeconds)} idle`} color={AMBER} />
      )}
      {has('off_account') && offAccountSeconds > 0 && (
        <Chip label={`⚠ ran on another sub-account ${fmtDur(offAccountSeconds)}`} color={AMBER} />
      )}
      {has('no_activity') && <Chip label="⚠ no activity" color={AMBER} />}
      {activePct != null && <Chip label={`${Math.round(activePct * 100)}% active`} muted />}
    </>
  );
}

// ── Loom ───────────────────────────────────────────────────────────
// A Loom share URL (loom.com/share/<id>) maps 1:1 to an embeddable player (loom.com/embed/<id>).
function loomEmbedUrl(url: string): string | null {
  const m = url.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/);
  return m ? `https://www.loom.com/embed/${m[1]}` : null;
}

// Inline responsive Loom player (16:10) — watched without leaving the app.
function LoomEmbed({ url }: { url: string }) {
  const embed = loomEmbedUrl(url);
  if (!embed) return null;
  return (
    <div className="relative mt-2 overflow-hidden rounded-lg border border-slate-200" style={{ paddingBottom: '62.5%', height: 0 }}>
      <iframe
        src={embed}
        title="Loom recording"
        allow="fullscreen"
        allowFullScreen
        className="absolute left-0 top-0 h-full w-full"
      />
    </div>
  );
}

// "Record Loom" button. Lazy-loads the Loom Record SDK from a CDN (esm.sh) the first time — kept
// OUT of the app bundle (its React-18 Atlaskit deps would clash with our React 19; loading it in an
// isolated module scope sidesteps that). Hidden entirely unless the worker returns a loomAppId.
const LOOM_SDK = 'https://esm.sh/@loomhq/record-sdk@4.12.4';
function LoomRecordButton({ onRecorded }: { onRecorded: (sharedUrl: string) => void }) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [appId, setAppId] = useState<string | null | undefined>(undefined); // undefined=loading, null=disabled
  const [status, setStatus] = useState<'init' | 'ready' | 'unsupported'>('init');
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  useEffect(() => {
    fetch('/api/config').then((r) => r.json()).then((d) => setAppId(d?.loomAppId ?? null)).catch(() => setAppId(null));
  }, []);

  useEffect(() => {
    if (!appId || !btnRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const [mod, supp] = await Promise.all([
          import(/* @vite-ignore */ LOOM_SDK),
          import(/* @vite-ignore */ `${LOOM_SDK}/is-supported`),
        ]);
        const { supported } = await supp.isSupported();
        if (cancelled) return;
        if (!supported) { setStatus('unsupported'); return; }
        const { configureButton } = await mod.setup({ publicAppId: appId });
        if (cancelled || !btnRef.current) return;
        const sdkButton = configureButton({ element: btnRef.current });
        sdkButton.on('insert-click', (video: { sharedUrl: string }) => onRecordedRef.current(video.sharedUrl));
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('unsupported');
      }
    })();
    return () => { cancelled = true; };
  }, [appId]);

  if (appId === null) return null;             // Loom not configured — hide the button
  if (status === 'unsupported') {
    return <span className="text-xs text-slate-400" title="Loom recording needs a Chromium-based browser">Loom unavailable</span>;
  }
  // The SDK attaches its recorder to this exact element once configured.
  return (
    <button
      ref={btnRef}
      type="button"
      disabled={status !== 'ready'}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:border-brand hover:text-brand disabled:cursor-wait disabled:opacity-60"
    >
      🎥 {status === 'ready' ? 'Record Loom' : 'Loading…'}
    </button>
  );
}

export default function TicketDetail({
  ticketId,
  meta,
  users,
  onBack,
  onChanged,
}: {
  ticketId: string;
  meta: TicketsMetaResponse;
  users: AgencyUser[];
  onBack: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<TicketDetailFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);

  function load() {
    setData(null);
    setError(null);
    agencyApi
      .ticket(ticketId)
      .then((d) => setData(d))
      .catch((e: Error) => setError(e.message));
  }
  useEffect(load, [ticketId]);

  const userName = (id: string | null) =>
    id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned (generic pot)';

  async function patch(field: 'status_id' | 'priority_id' | 'category_id' | 'assigned_to', raw: string) {
    if (!data) return;
    const value = field === 'assigned_to' && raw === UNASSIGNED ? null : raw || null;
    // optimistic
    setData({ ...data, ticket: { ...data.ticket, [field]: value } });
    try {
      await agencyApi.patchTicket(ticketId, { [field]: value });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  }

  async function submitComment() {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await agencyApi.addComment(ticketId, {
        body: comment.trim(),
        author_type: 'agent',
        author_name: 'Agency',
        is_internal: internal,
      });
      setComment('');
      setInternal(false);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPosting(false);
    }
  }

  // Loom recorded → attach it to the ticket as an agent comment (the customer sees it in-thread).
  async function attachLoom(sharedUrl: string) {
    try {
      await agencyApi.addComment(ticketId, { body: '', author_type: 'agent', author_name: 'Agency', loom_url: sharedUrl });
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (error) return <div className="space-y-4"><Button variant="ghost" onClick={onBack}>← Back to tickets</Button><ErrorNote error={error} /></div>;
  if (!data) return <Spinner />;
  if (!data.ticket) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>← Back to tickets</Button>
        <ErrorNote error="Ticket not found." />
      </div>
    );
  }

  const t = data.ticket;
  const status = meta.statuses.find((m) => m.id === t.status_id);
  const priority = meta.priorities.find((m) => m.id === t.priority_id);

  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={onBack}>← Back to tickets</Button>

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        {/* Main */}
        <div className="space-y-5">
          <Card className="p-5">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {status && <Chip label={status.label} color={status.color} />}
              {priority && <Chip label={priority.label} color={priority.color} />}
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t.subject}</h1>
            <div className="mt-1 text-xs text-slate-400">
              {t.requester_name ?? 'Anonymous'}
              {t.requester_email ? ` · ${t.requester_email}` : ''} · {formatApiTime(t.created_at)}
            </div>
            {(t.context_area || t.context_route) && (
              <div className="mt-2 text-xs text-slate-500">
                Context: <span className="font-medium">{t.context_area ?? t.context_route}</span>
              </div>
            )}
            {t.body && <p className="mt-4 whitespace-pre-wrap text-sm text-slate-700">{t.body}</p>}
          </Card>

          {/* Requester + sub-account context and time tracker */}
          <RequesterPanel ticketId={ticketId} />
          <RecentChangesPanel locationId={t.location_id} />
          <TimeTracker ticketId={ticketId} defaultBillable />

          {/* Thread */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Conversation</h3>
            {data.comments.length === 0 ? (
              <p className="text-sm text-slate-400">No comments yet.</p>
            ) : (
              <div className="space-y-3">
                {data.comments.map((c) => (
                  <Card
                    key={c.id}
                    className={`p-4 ${c.is_internal ? 'border-amber-200 bg-amber-50' : ''}`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                      <span className="font-medium text-slate-700">{c.author_name ?? c.author_type}</span>
                      <Chip label={c.author_type} muted />
                      {c.is_internal && <Chip label="internal note" color="#d97706" />}
                      <span className="ml-auto">{formatApiTime(c.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-slate-700">{c.body}</p>
                    {c.loom_url && <LoomEmbed url={c.loom_url} />}
                  </Card>
                ))}
              </div>
            )}

            {/* New comment */}
            <Card className="mt-4 p-4">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Write a reply…"
                className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
              <div className="mt-2 flex items-center justify-between">
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                    className="rounded border-slate-300 text-brand focus:ring-brand"
                  />
                  Internal note (not shown to customer)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <LoomRecordButton onRecorded={attachLoom} />
                  <Button onClick={submitComment} disabled={posting || !comment.trim()}>
                    {posting ? 'Sending…' : 'Send reply'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Rich context, attachments, and viewing history */}
          <ContextPanel ticketId={ticketId} context={data.context} />
          <AttachmentsPanel ticketId={ticketId} attachments={data.attachments} />
          <ViewedContentPanel items={data.viewedContent} />
        </div>

        {/* Sidebar controls */}
        <Card className="h-fit space-y-4 p-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Status</label>
            <Select value={t.status_id ?? ''} onChange={(v) => patch('status_id', v)} className="w-full">
              <option value="">—</option>
              {meta.statuses.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Priority</label>
            <Select value={t.priority_id ?? ''} onChange={(v) => patch('priority_id', v)} className="w-full">
              <option value="">—</option>
              {meta.priorities.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Category</label>
            <Select value={t.category_id ?? ''} onChange={(v) => patch('category_id', v)} className="w-full">
              <option value="">—</option>
              {meta.categories.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Assignee</label>
            <Select
              value={t.assigned_to ?? UNASSIGNED}
              onChange={(v) => patch('assigned_to', v)}
              className="w-full"
            >
              <option value={UNASSIGNED}>Unassigned (generic pot)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-slate-400">Currently: {userName(t.assigned_to)}</p>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── Context panel ──────────────────────────────────────────────────
// Compact key/value view of what the browser reported at submit time.
// Hidden entirely when no context was captured.
function ContextPanel({ ticketId, context }: { ticketId: string; context: TicketContext | null }) {
  if (!context) return null;
  const c = context;
  const screen = c.screen ? `${c.screen.w}×${c.screen.h}` : DASH;
  const viewport = c.viewport ? `${c.viewport.w}×${c.viewport.h}` : DASH;
  // Tag the link with the agent-drawer marker, same as the requester-panel hotlink —
  // landing on the page WITHOUT it gives the agent no drawer and no timer.
  const agentUrl = c.url ? `${c.url.split('#')[0]}#ss-agent=${ticketId}` : null;
  const rows: [string, ReactNode][] = [
    ['Browser', c.browser || DASH],
    ['OS', c.os || DASH],
    ['Screen', screen],
    ['Viewport', viewport],
    ['Pixel ratio', c.dpr != null ? String(c.dpr) : DASH],
    ['Route', c.route || DASH],
    ['Feature group', c.featureGroup || DASH],
    ['Area', c.area || DASH],
    [
      'Page URL',
      agentUrl ? (
        <a
          href={agentUrl}
          target="_blank"
          rel="noreferrer"
          className="break-all text-brand hover:underline"
        >
          {c.url}
        </a>
      ) : (
        DASH
      ),
    ],
    ['Referrer', c.referrer ? <span className="break-all">{c.referrer}</span> : DASH],
    ['Language', c.language || DASH],
    ['Captured', formatApiTime(c.capturedAt, DASH)],
  ];
  return (
    <Card className="p-5">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">Context</h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col gap-0.5">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{k}</dt>
            <dd className="text-sm text-slate-700">{v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ── Attachments panel ──────────────────────────────────────────────
// Screenshots render inline (click to open full size); files are download links.
function AttachmentsPanel({
  ticketId,
  attachments,
}: {
  ticketId: string;
  attachments: TicketAttachment[];
}) {
  return (
    <Card className="p-5">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">Attachments</h3>
      {attachments.length === 0 ? (
        <p className="text-sm text-slate-400">No attachments.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {attachments.map((a) => {
            const url = agencyApi.attachmentUrl(ticketId, a.id);
            if (a.kind === 'screenshot') {
              return <ScreenshotThumb key={a.id} url={url} filename={a.filename} />;
            }
            return (
              <AuthedDownload
                key={a.id}
                url={url}
                filename={a.filename}
                className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:border-brand hover:bg-slate-50"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded bg-slate-100 text-slate-500">
                  ↓
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-700">
                    {a.filename ?? 'File'}
                  </span>
                  <span className="block text-xs text-slate-400">{fmtSize(a.size)}</span>
                </span>
              </AuthedDownload>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Authenticated attachment rendering ─────────────────────────────
//
// Attachments are served by an endpoint gated on the session Bearer token — and a browser will
// NOT attach that header to an `<img src>` or a `target="_blank"` link. Every screenshot rendered
// as a broken image and every download 401'd. So we fetch the bytes WITH auth and hand the UI an
// object URL instead. Keeping the endpoint bearer-only (rather than adding a signed query token)
// means attachments never become reachable by URL alone.
function useAuthedObjectUrl(url: string): { href: string | null; failed: boolean } {
  const [href, setHref] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setHref(null);
    setFailed(false);
    (async () => {
      try {
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setHref(created);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);   // don't leak blobs as the agent opens tickets
    };
  }, [url]);

  return { href, failed };
}

function ScreenshotThumb({ url, filename }: { url: string; filename: string | null }) {
  const { href, failed } = useAuthedObjectUrl(url);
  if (failed) {
    return (
      <div className="grid h-32 w-40 place-items-center rounded-lg border border-dashed border-rose-200 bg-rose-50 px-2 text-center text-xs text-rose-600">
        Could not load {filename ?? 'screenshot'}
      </div>
    );
  }
  if (!href) {
    return <div className="h-32 w-40 animate-pulse rounded-lg border border-slate-200 bg-slate-100" />;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={filename ?? 'Screenshot'}
      className="group block overflow-hidden rounded-lg border border-slate-200 hover:border-brand"
    >
      <img src={href} alt={filename ?? 'Screenshot'} className="h-32 w-auto max-w-[16rem] object-cover" />
    </a>
  );
}

function AuthedDownload({
  url, filename, className, children,
}: { url: string; filename: string | null; className?: string; children: ReactNode }) {
  const { href, failed } = useAuthedObjectUrl(url);
  if (failed) {
    return <div className={`${className} opacity-60`} title="Could not load this attachment">{children}</div>;
  }
  return (
    <a
      href={href ?? undefined}
      download={filename ?? undefined}
      target="_blank"
      rel="noreferrer"
      className={className}
      aria-busy={!href}
    >
      {children}
    </a>
  );
}

// ── Viewing history panel ──────────────────────────────────────────
// What the requester viewed/watched before opening the ticket.
function ViewedContentPanel({ items }: { items: ViewedContent[] }) {
  return (
    <Card className="p-5">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">What they've tried</h3>
      <p className="mb-3 text-xs text-slate-400">
        Content the requester viewed before opening this ticket.
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">Nothing viewed before this ticket.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((v) => (
            <li key={v.contentId} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-700">{v.title}</span>
                  <Chip label={v.type} color={CONTENT_ACCENT[v.type]} />
                </div>
                <div className="mt-0.5 text-xs text-slate-400">
                  {v.area || 'General'} · {fmtRelative(v.lastAt)}
                </div>
              </div>
              <div className="shrink-0 text-right text-xs text-slate-500">
                <div className="tabular-nums">
                  {v.views} view{v.views === 1 ? '' : 's'}
                </div>
                {v.type === 'video' && (
                  <div className="tabular-nums text-slate-400">{fmtWatch(v.totalSeconds)} watched</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Requester & sub-account panel ──────────────────────────────────
// Who + where the ticket came from: sub-account, client, current plan, assigned
// agent, an "open in sub-account" hotlink, burden stats, and recent tickets.
function RequesterPanel({ ticketId }: { ticketId: string }) {
  const [ctx, setCtx] = useState<RequesterContext | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCtx(null);
    setError(null);
    agencyApi
      .requester(ticketId)
      .then(setCtx)
      .catch((e: Error) => setError(e.message));
  }, [ticketId]);

  if (error)
    return (
      <Card className="p-5">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Requester &amp; sub-account</h3>
        <p className="text-sm text-slate-400">Couldn’t load requester context.</p>
      </Card>
    );
  if (!ctx) return <Card className="p-5"><Spinner label="Loading requester…" /></Card>;

  const sa = ctx.subAccount;
  const b = ctx.subAccountBurden;
  const u = ctx.userBurden;

  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-700">Requester &amp; sub-account</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-900">
              {sa.client_name ?? 'Unknown sub-account'}
            </span>
            {sa.plan && <Chip label={sa.plan.name} color="#2563eb" />}
            {sa.location_id && (
              <span className="font-mono text-xs text-slate-400">{sa.location_id}</span>
            )}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Assigned agent: <span className="font-medium text-slate-700">{sa.assignedAgent?.name ?? 'Unassigned'}</span>
          </div>
        </div>
        {sa.hotlink && (
          <Button
            variant="secondary"
            onClick={() => window.open(sa.hotlink!, '_blank', 'noopener,noreferrer')}
          >
            Open in sub-account ↗
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label="SA tickets" value={b.totalTickets} hint={`${b.open} open · ${b.resolved} resolved`} />
        <StatTile label="Last 30 days" value={b.last30d} />
        <StatTile
          label="Avg resolution"
          value={b.avgResolutionHours != null ? `${b.avgResolutionHours.toFixed(1)}h` : DASH}
        />
      </div>

      <div className="mt-4 rounded-lg bg-slate-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-400">This user</div>
        <div className="mt-1 text-sm text-slate-700">
          <span className="font-medium">{u.name ?? 'Anonymous'}</span>
          {u.email ? <span className="text-slate-400"> · {u.email}</span> : null}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {u.totalTickets} ticket{u.totalTickets === 1 ? '' : 's'} · {u.open} open
        </div>
      </div>

      {ctx.recentTickets.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Recent tickets</div>
          <ul className="divide-y divide-slate-100">
            {ctx.recentTickets.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700" title={r.subject}>
                  {r.subject}
                </span>
                {r.status && <Chip label={r.status} muted />}
                <span className="shrink-0 text-xs text-slate-400">{fmtRelative(r.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Recent workflow changes panel ──────────────────────────────────
// The workflow change-log for the ticket's sub-account — the last few captured
// workflow edits, each with who made it and exactly what changed. Lets an agent
// answer "did something change right before this ticket?" without leaving the
// ticket. Hidden entirely when there's no location or no captured changes.
function RecentChangesPanel({ locationId }: { locationId: string | null }) {
  const [changes, setChanges] = useState<WorkflowRevision[] | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!locationId) {
      setChanges(null);
      return;
    }
    setChanges(null);
    agencyApi
      .workflowChangelog({ locationId, limit: 5 })
      .then(setChanges)
      .catch(() => setChanges([]));
  }, [locationId]);

  if (!locationId || !changes || changes.length === 0) return null;

  return (
    <Card className="p-5">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Recent workflow changes</h3>
      <p className="mb-3 text-xs text-slate-400">
        Workflow edits in this sub-account — check if something changed right before this ticket.
      </p>
      <ul className="divide-y divide-slate-100">
        {changes.map((r) => {
          const bullets = r.changes ?? [];
          const open = !!expanded[r.id];
          const shown = open ? bullets : bullets.slice(0, 3);
          return (
            <li key={r.id} className="py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium text-slate-700">{r.name ?? 'Untitled workflow'}</span>
                {r.status && <Chip label={r.status} muted />}
                <span className="ml-auto shrink-0 text-xs text-slate-400">{fmtRelative(r.createdAt)}</span>
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                by <span className="font-medium text-slate-600">{r.updatedByName ?? 'Unknown'}</span>
              </div>
              {shown.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {shown.map((ch, i) => (
                    <li key={i} className="text-xs text-slate-600">
                      <span className="mr-1.5 text-slate-300">•</span>
                      {ch}
                    </li>
                  ))}
                </ul>
              )}
              {bullets.length > 3 && (
                <button
                  type="button"
                  onClick={() => setExpanded((e) => ({ ...e, [r.id]: !open }))}
                  className="mt-1 text-xs font-medium text-brand hover:underline"
                >
                  {open ? 'Show less' : `+${bullets.length - 3} more change${bullets.length - 3 === 1 ? '' : 's'}`}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ── Time tracker ───────────────────────────────────────────────────
// Prominent Start → live HH:MM:SS with pause/resume/stop, billable toggle, and
// an optional note. Below it, the ticket's logged time entries. The live count
// ticks locally (1s) off the running timer the Worker reports for this agent.
function TimeTracker({ ticketId, defaultBillable }: { ticketId: string; defaultBillable: boolean }) {
  const [running, setRunning] = useState<RunningTimer | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [note, setNote] = useState('');
  const [billable, setBillable] = useState(defaultBillable);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const baseRef = useRef<{ elapsed: number; at: number } | null>(null);

  const active = running && running.ticketId === ticketId ? running : null;
  const otherActive = running && running.ticketId !== ticketId ? running : null;

  function syncRunning(r: RunningTimer | null) {
    setRunning(r);
    baseRef.current = r ? { elapsed: r.elapsedSeconds, at: Date.now() } : null;
    if (r && r.ticketId === ticketId) setBillable(r.entry.billable);
  }

  function loadEntries() {
    agencyApi.ticketTime(ticketId).then(setEntries).catch(() => {});
  }

  useEffect(() => {
    const agent = actingAgentId();
    if (agent) agencyApi.timeRunning(agent).then(syncRunning).catch(() => {});
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Live 1s tick while running and not paused.
  useEffect(() => {
    if (!active || active.paused) return;
    const iv = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [active, active?.paused]);

  const displayed = (() => {
    if (!active || !baseRef.current) return 0;
    if (active.paused) return baseRef.current.elapsed;
    return baseRef.current.elapsed + (Date.now() - baseRef.current.at) / 1000;
  })();

  async function start() {
    const agent = actingAgentId();
    if (!agent) { setError('No agent identity resolved — reload the app inside HighLevel.'); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await agencyApi.timeStart(ticketId, {
        agent_id: agent,
        note: note.trim() || null,
        billable,
      });
      syncRunning(r);
      loadEntries();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function pauseResume() {
    if (!active) return;
    setBusy(true);
    try {
      const fn = active.paused ? agencyApi.timeResume : agencyApi.timePause;
      const entry = await fn(active.entry.id);
      syncRunning({ ...active, entry, paused: entry.paused, elapsedSeconds: entry.seconds });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    if (!active) return;
    setBusy(true);
    try {
      await agencyApi.timeStop(active.entry.id);
      syncRunning(null);
      setNote('');
      loadEntries();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleBillable(v: boolean) {
    setBillable(v);
    if (active) {
      try {
        await agencyApi.timePatch(active.entry.id, { billable: v });
      } catch {
        /* best-effort; UI already reflects intent */
      }
    }
  }

  const totalLogged = entries.reduce((sum, e) => sum + e.seconds, 0);

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-700">Time tracker</h3>
        {totalLogged > 0 && (
          <span className="text-xs text-slate-400">{fmtHMS(totalLogged)} logged</span>
        )}
      </div>

      {otherActive && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700">
          A timer is running on another ticket ({otherActive.ticketSubject}). Starting here pauses it.
        </div>
      )}
      {error && <div className="mb-3 text-xs text-rose-600">{error}</div>}

      <div className="flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 p-4">
        {active ? (
          <>
            <div
              className={`font-mono text-3xl font-semibold tabular-nums ${
                active.paused ? 'text-slate-400' : 'text-slate-900'
              }`}
            >
              {fmtHMS(displayed)}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={pauseResume} disabled={busy}>
                {active.paused ? 'Resume' : 'Pause'}
              </Button>
              <Button variant="danger" onClick={stop} disabled={busy}>
                Stop
              </Button>
            </div>
            {active.paused && <Chip label="paused" color="#d97706" />}
          </>
        ) : (
          <>
            <Button onClick={start} disabled={busy}>
              {busy ? 'Starting…' : '▶ Start timer'}
            </Button>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)…"
              className="min-w-[12rem] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </>
        )}
        <div className="ml-auto">
          <Toggle checked={billable} onChange={toggleBillable} label="Billable" />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Logged time</div>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-400">No time logged yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {entries.map((e) => (
              <li key={e.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">
                      {e.agent_name ?? 'Agent'}
                    </span>
                    {e.running && <Chip label="running" color="#16a34a" />}
                    {!e.billable && <Chip label="non-billable" muted />}
                    <IntegrityChips integrity={e.integrity} />
                  </div>
                  {e.note && <div className="mt-0.5 truncate text-xs text-slate-400" title={e.note}>{e.note}</div>}
                </div>
                <span className="shrink-0 font-mono text-sm tabular-nums text-slate-700">
                  {fmtHMS(e.seconds)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
